import React, { useEffect, useMemo } from 'react';
import SelectLoader from './SelectLoader';
import { getIntl } from '../../../../utils/intl';
import useCareTeamMembers from '../../../../hooks/api/useCareTeamMembers';
import getName from '../../../../utils/getName';
import { isInsurance } from '../../../../utils/careTeamUtils';
import {
    CareTeamDomainNoteDestination,
    NoteDestination,
    isCareTeamDomainNoteDestination,
    isCareTeamNoteDestination,
} from '../utils/types';
import Stack from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';
import Select from '../../../v2BaseComponents/Inputs/Select';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';

const intl = getIntl('profile-select', {
    selectCareTeamMember: 'Select Care Team Member (optional)',
    noCareTeam: 'No care team for this profile.  You can still move the note to the care team section.',
    optionalMessage: 'if no value selected, the note will be moved to the care team section of the profile',
});

export type CareTeamMemberSelectOwnProps = {
    value: CareTeamDomainNoteDestination;
    onChange: (value: CareTeamDomainNoteDestination) => void;
    isDisabled?: boolean;
};

export type CareTeamMemberSelectProps = CareTeamMemberSelectOwnProps;

export const shouldRenderCareTeamMemberSelect = (value: NoteDestination): value is CareTeamDomainNoteDestination => {
    return isCareTeamDomainNoteDestination(value);
};

const CareTeamMemberSelect: React.FC<CareTeamMemberSelectProps> = ({ value, onChange, isDisabled }) => {
    const { careTeamMembers, isLoadingCareTeamMembers } = useCareTeamMembers({
        medicalProfileId: value.medicalProfileId,
    });

    const options: SelectOption[] = useMemo(() => {
        if (isLoadingCareTeamMembers) return [];

        return careTeamMembers
            .map((_) => {
                const label = isInsurance(_)
                    ? _.metadata.insuranceProviderName
                    : getName(_.contactDetails) || _.contactDetails.company || '';
                return {
                    label,
                    value: _.careTeamMemberId,
                    testID: label,
                };
            })
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    }, [careTeamMembers, isLoadingCareTeamMembers]);

    const maybeInvalidValue = useMemo<string | null | 'invalid-value'>(() => {
        if (isLoadingCareTeamMembers) return null;

        if (isCareTeamNoteDestination(value)) return null;

        const targetCareTeamMember = careTeamMembers.find((_) => _.careTeamMemberId === value.entityId);

        if (!targetCareTeamMember) return 'invalid-value';

        return value.entityId;
    }, [isLoadingCareTeamMembers, value, careTeamMembers]);

    useEffect(() => {
        if (maybeInvalidValue === 'invalid-value') {
            onChange({
                medicalProfileId: value.medicalProfileId,
                entityType: 'care-team',
            });
        }
    }, [maybeInvalidValue, onChange, value, value.medicalProfileId]);

    if (isLoadingCareTeamMembers || maybeInvalidValue === 'invalid-value') return <SelectLoader />;

    if (options.length === 0) {
        return (
            <Stack horizontal fullWidth justify="center">
                <Text variant="body-1">{intl('noCareTeam')}</Text>
            </Stack>
        );
    }

    return (
        <Select
            title={intl('selectCareTeamMember')}
            selectOptions={options}
            value={maybeInvalidValue}
            onChange={(selectValue) => {
                if (selectValue === null) {
                    onChange({
                        medicalProfileId: value.medicalProfileId,
                        entityType: 'care-team',
                    });
                } else {
                    onChange({
                        medicalProfileId: value.medicalProfileId,
                        entityType: 'care-team-member',
                        entityId: selectValue,
                    });
                }
            }}
            isDisabled={isDisabled}
            testID="note-transfer-care-team-member-select"
        />
    );
};

export default React.memo(CareTeamMemberSelect);
